'use client';

import { userHasPermission } from '@ferty9/auth';
import { APP_PLATFORM_NAME } from '@ferty9/constants';
import type { AccessPermission, AccessRole, AdminUserPayload, AdminUserSession } from '@ferty9/types';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createAccessRole, deleteAccessRole, getAccessPermissions, getAccessRoles, getAssignableAccessRoles, updateAccessRole } from './lib/api/access-control';
import { getCenters } from './lib/api/centers';
import { downloadBlob, exportDashboardPdfReport, getDashboard } from './lib/api/dashboard';
import { deleteFeedback as deleteFeedbackRequest, getFeedback, getFeedbackPage, type FeedbackPagination } from './lib/api/feedback';
import { createQuestion as createQuestionRequest, deleteQuestion as deleteQuestionRequest, getQuestions, updateQuestion as updateQuestionRequest } from './lib/api/questions';
import { getBenchmark, getDetailedMonthlySummary, getQuestionWise, getRegistrationSummary } from './lib/api/reports';
import {
  activateAutomationWorkflow,
  createAutomationWorkflow,
  createGeneralSettingsGroup,
  copyAutomationWorkflow,
  deactivateAutomationWorkflow,
  deleteAutomationWorkflow,
  deleteGeneralSettingsGroup,
  getGeneralSettings,
  updateAutomationWorkflow,
  updateGeneralSettingsGroup,
} from './lib/api/general-settings';
import { createTeamMember, deleteTeamMember, getTeamMembers, updateTeamMember } from './lib/api/staff';
import {
  deleteIntegrationSetting as deleteIntegrationSettingRequest,
  getExotelWhatsappTemplates,
  getIntegrationSettings,
  syncExotelWhatsappTemplate as syncExotelWhatsappTemplateRequest,
  updateIntegrationSetting as updateIntegrationSettingRequest,
} from './lib/api/system-settings';
import {
  createUser as createUserRequest,
  deleteUser as deleteUserRequest,
  forceLogoutUser as forceLogoutUserRequest,
  forceLogoutUsersByRole as forceLogoutUsersByRoleRequest,
  getUsers,
  getBusinessReportTargetAccessOptions,
  unlockUserLogin as unlockUserLoginRequest,
  resendUserWelcome as resendUserWelcomeRequest,
  updateUser as updateUserRequest,
} from './lib/api/users';
import { sendAdminVerificationOtp, verifyAdminVerificationOtp } from './lib/api/admin-otp';
import { confirmPasswordChange, sendPasswordChangeOtp } from './lib/api/auth';
import { ADMIN_SESSION_EXPIRED_EVENT, ApiError } from './lib/api/client';
import { exportElementAsPdf, summaryPdfFileName } from './lib/export/pdf';
import { exportDetailedMonthlySummaryTable, exportFeedbackRows, exportMonthlySummaryTable, exportQuestionWiseRows } from './lib/export/xlsx';
import { getAdminTheme, setAdminTheme } from './lib/storage/theme';
import { getBusinessReportNavigation, type BusinessReportNavigationItem } from './lib/api/business-reports';
import { AdminShell } from './components/AdminShell';
import { ModulePageHost } from './modules/ModulePageHost';
import { ModuleManagementEntry } from './modules/ModuleManagementEntry';
import { ModuleExtensionSurface } from './modules/ModuleExtensionSurface';
import { useModuleWorkspace, MODULE_SESSION_CLEAR } from './modules/useModuleWorkspace';
import { moduleTab, moduleTabFromPath } from './modules/types';
import { BusinessReportDataSourcesWorkspace } from './components/BusinessReportDataSourcesWorkspace';
import { BusinessReportAdministrationWorkspace } from './components/BusinessReportAdministrationWorkspace';
import { BusinessRevenueOperationsWorkspace } from './components/BusinessRevenueOperationsWorkspace';
import { BusinessTargetsWorkspace } from './components/BusinessTargetsWorkspace';
import { BusinessGovernanceOwnershipTab } from './components/BusinessGovernanceOwnershipTab';
import { BusinessDailyRevenueTab } from './components/BusinessDailyRevenueTab';
import { BusinessMedicsRevenueTab } from './components/BusinessMedicsRevenueTab';
import { BusinessOperationalMonitoringTab } from './components/BusinessOperationalMonitoringTab';
import { BusinessDataIntegrityTab } from './components/BusinessDataIntegrityTab';
import { BusinessReportUnavailableTab } from './components/BusinessReportUnavailableTab';
import { DashboardTab } from './components/DashboardTab';
import { ClinicalDashboardTab } from './components/ClinicalDashboardTab';
import { BusinessDashboardTab } from './components/BusinessDashboardTab';
import { DoctorReportTab } from './components/DoctorReportTab';
import { DailyClinicalReportTab } from './components/DailyClinicalReportTab';
import { DoctorKpiReportTab } from './components/DoctorKpiReportTab';
import { FcdrReportTab } from './components/FcdrReportTab';
import { WaterfallReportTab } from './components/WaterfallReportTab';
import { BtlReportTab, DoctorIvfPerformanceTab, MarketingReportTab } from './components/NativeReportScaffolds';
import { DetailedMonthlySummaryTab } from './components/DetailedMonthlySummaryTab';
import { FeedbackTrackerTab } from './components/FeedbackTrackerTab';
import { GeneralSettingsTab } from './components/GeneralSettingsTab';
import { OfferCampaignsTab } from './components/OfferCampaignsTab';
import { RoleManagementTab } from './components/RoleManagementTab';
import { SystemSettingsTab } from './components/SystemSettingsTab';
import { AdminAlertModal, AdminConfirmModal, Loader, type AdminModalVariant } from './components/common';
import { FeedbackResponsesTab, MonthlySummaryTab, QuestionWiseTab, StaffManagementTab, SurveyQuestionsTab, UserAccountsTab } from './components/tabs';
import type { AdminActions, AdminTab, DashboardExportFilters, StaticAdminTab } from './types';
import { currentMonth, feedbackExportRows, questionWiseRows, toExportRows } from './utils';
import { businessReportRoute, normalizeBusinessReportPath, portalSafeBusinessReportRoute } from './lib/business-report-routes';
import { createOfferCampaign } from './lib/api/offer-campaigns';
import type { OfferCampaignAdminPayload } from './types/offer-campaigns';

const adminTabs: StaticAdminTab[] = ['dashboard', 'clinical-dashboard', 'business-dashboard', 'doctor-report', 'daily-clinical-report', 'doctor-kpi-report', 'doctor-ivf-performance', 'fcdr-report', 'waterfall-report', 'marketing-report', 'btl-report', 'business-data-integrity', 'business-report-administration', 'business-report-centers', 'business-report-financial-calendar', 'business-report-sources', 'business-revenue-sources', 'business-revenue-source-definitions', 'business-revenue-category-rules', 'business-governance-ownership', 'business-report-governance', 'business-manual-revenue', 'business-monthly-targets', 'business-file-imports', 'business-reconciliation', 'business-revenue-operations', 'business-targets-workspace', 'business-operational-monitoring', 'feedback-tracker', 'offer-campaigns', 'feedback', 'question-wise', 'summary', 'detailed-summary', 'staff', 'users', 'questions', 'roles', 'general-settings', 'settings'];

const viewTitles: Partial<Record<StaticAdminTab | 'module-management', string>> = {
  dashboard: 'NPS Dashboard', 'clinical-dashboard': 'Clinical Dashboard', 'business-dashboard': 'Business Dashboard',
  'doctor-report': 'Doctor Report', 'daily-clinical-report': 'Daily Clinical Report', 'doctor-kpi-report': 'Doctor KPI Report',
  'doctor-ivf-performance': 'Doctor-wise IVF Performance', 'fcdr-report': 'Financial and Clinical Daily Report',
  'waterfall-report': 'Registration to OPU Waterfall', 'marketing-report': 'Marketing Overview', 'btl-report': 'BTL Report',
  'feedback-tracker': 'Feedback Tracker', 'offer-campaigns': 'Offer Campaigns', feedback: 'Feedback Responses',
  'question-wise': 'Question-wise Report', summary: 'Monthly Summary', 'detailed-summary': 'Detailed Summary',
  staff: 'Team Members', users: 'User Accounts', questions: 'Survey Questions', roles: 'Roles and Access',
  'general-settings': 'General Settings', settings: 'Integration Settings',
  'business-report-administration': 'Business Reports Administration', 'business-report-sources': 'Business Reports Data Sources',
  'business-revenue-operations': 'Revenue Operations', 'business-targets-workspace': 'Business Targets',
  'business-data-integrity': 'Business Data Integrity', 'business-operational-monitoring': 'Business Operations Monitoring',
  'module-management': 'Module Management',
};

function isBusinessReportTab(tab: string): tab is `business-report:${string}` {
  return tab.startsWith('business-report:');
}

function isKnownAdminTab(tab: string): tab is AdminTab {
  return adminTabs.includes(tab as StaticAdminTab) || isBusinessReportTab(tab) || tab==='module-management' || /^module:[a-z][a-z0-9-]*:[a-z][a-z0-9-]*$/.test(tab);
}

function adminDateRangeParams(value: string, from: string, to: string) {
  return {
    dateRange: value,
    days: /^\d+$/.test(value) ? value : undefined,
    from: value === 'CUSTOM' ? from : undefined,
    to: value === 'CUSTOM' ? to : undefined,
  };
}

function feedbackDateRangeParams(value: string, from: string, to: string, options?: { validateCustomDate?: boolean }) {
  if (value === 'CUSTOM' && (!from || !to || from > to)) {
    if (options?.validateCustomDate) {
      throw new Error('Custom date range requires valid from and to dates.');
    }
    return null;
  }
  return adminDateRangeParams(value, from, to);
}

function dateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function currentMonthDateInputs() {
  const today = new Date();
  return {
    from: dateInputValue(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: dateInputValue(today),
  };
}

interface PasswordChangeState {
  open: boolean;
  forced: boolean;
  step: 'password' | 'otp';
  newPassword: string;
  confirmPassword: string;
  otp: string;
  channel: 'email' | 'whatsapp';
  maskedPhone?: string;
  sending: boolean;
  submitting: boolean;
  error: string;
  message: string;
}

const PASSWORD_MIN_LENGTH = 12;

const initialPasswordChangeState: PasswordChangeState = {
  open: false,
  forced: false,
  step: 'password',
  newPassword: '',
  confirmPassword: '',
  otp: '',
  channel: 'whatsapp',
  maskedPhone: undefined,
  sending: false,
  submitting: false,
  error: '',
  message: '',
};

const DAILY_REVENUE_ROUTE = '/dashboard/business-reports/daily-revenue';
const DAILY_REVENUE_TAB: AdminTab = 'business-report:DAILY_REVENUE_SNAPSHOT';
const CLINICAL_DASHBOARD_ROUTE = '/dashboard/clinical';
const CLINICAL_DASHBOARD_TAB: AdminTab = 'clinical-dashboard';
const BUSINESS_DASHBOARD_ROUTE = '/dashboard/business';
const BUSINESS_DASHBOARD_TAB: AdminTab = 'business-dashboard';
const DOCTOR_KPI_ROUTE = '/reports/clinical/doctor-kpi';
const DOCTOR_KPI_TAB: AdminTab = 'doctor-kpi-report';
const FCDR_ROUTE = '/reports/business/fcdr';
const FCDR_TAB: AdminTab = 'fcdr-report';
const WATERFALL_ROUTE = '/reports/business/waterfall';
const WATERFALL_TAB: AdminTab = 'waterfall-report';
const ADMIN_DASHBOARD_ROUTE = '/dashboard';
const REPORTS_DASHBOARD_ROUTE = '/reports/dashboard';
const DASHBOARD_SHELL_ROUTES = new Set([ADMIN_DASHBOARD_ROUTE, REPORTS_DASHBOARD_ROUTE]);
const REPORT_WIDE_ROUTES = new Set([
  REPORTS_DASHBOARD_ROUTE,
  DAILY_REVENUE_ROUTE,
  CLINICAL_DASHBOARD_ROUTE,
  BUSINESS_DASHBOARD_ROUTE,
  DOCTOR_KPI_ROUTE,
  FCDR_ROUTE,
  WATERFALL_ROUTE,
  '/reports/clinical/doctor',
  '/reports/clinical/daily',
  '/reports/clinical/doctor-wise-ivf',
  '/reports/marketing/overview',
  '/reports/marketing/btl',
]);
const REPORT_WIDE_TABS = new Set<AdminTab>([
  DAILY_REVENUE_TAB,
  CLINICAL_DASHBOARD_TAB,
  BUSINESS_DASHBOARD_TAB,
  DOCTOR_KPI_TAB,
  FCDR_TAB,
  WATERFALL_TAB,
  'doctor-report',
  'daily-clinical-report',
  'doctor-ivf-performance',
  'marketing-report',
  'btl-report',
  'offer-campaigns',
]);
const isWideWorkspaceTab=(tab:AdminTab)=>REPORT_WIDE_TABS.has(tab)||tab.startsWith('module:assets-mgmt:');

const STATIC_ROUTE_TABS = new Map<string, AdminTab>([
  [DAILY_REVENUE_ROUTE, DAILY_REVENUE_TAB],
  [CLINICAL_DASHBOARD_ROUTE, CLINICAL_DASHBOARD_TAB],
  [BUSINESS_DASHBOARD_ROUTE, BUSINESS_DASHBOARD_TAB],
  [DOCTOR_KPI_ROUTE, DOCTOR_KPI_TAB],
  [FCDR_ROUTE, FCDR_TAB],
  [WATERFALL_ROUTE, WATERFALL_TAB],
  ['/reports/clinical/doctor', 'doctor-report'],
  ['/reports/clinical/daily', 'daily-clinical-report'],
  ['/reports/clinical/doctor-wise-ivf', 'doctor-ivf-performance'],
  ['/reports/marketing/overview', 'marketing-report'],
  ['/reports/marketing/btl', 'btl-report'],
  ['/dashboard/business-reports/administration', 'business-report-administration'],
  ['/dashboard/business-reports/data-sources', 'business-report-sources'],
  ['/dashboard/business-reports/centre-master', 'business-report-administration'],
  ['/dashboard/business-reports/financial-calendar', 'business-report-administration'],
  ['/dashboard/business-reports/revenue-sources', 'business-report-sources'],
  ['/dashboard/business-reports/revenue-category-rules', 'business-report-sources'],
  ['/dashboard/business-reports/source-onboarding', 'business-report-sources'],
  ['/dashboard/business-reports/revenue-operations', 'business-revenue-operations'],
  ['/dashboard/business-reports/validation', 'business-revenue-operations'],
  ['/dashboard/business-reports/manual-revenue', 'business-revenue-operations'],
  ['/dashboard/business-reports/governance-ownership', 'business-governance-ownership'],
  ['/dashboard/business-reports/target-governance', 'business-targets-workspace'],
  ['/dashboard/business-reports/monthly-targets', 'business-targets-workspace'],
  ['/dashboard/business-reports/targets', 'business-targets-workspace'],
  ['/dashboard/business-reports/file-imports', 'business-revenue-operations'],
  ['/dashboard/business-reports/reconciliation', 'business-revenue-operations'],
  ['/dashboard/business-reports/operations', 'business-operational-monitoring'],
  ['/dashboard/business-reports/data-integrity', 'business-data-integrity'],
]);

function requestedAdminTabFromLocation(): AdminTab {
  if (typeof window === 'undefined') return 'dashboard';
  const path = normalizeBusinessReportPath(window.location.pathname);
  const modulePath=moduleTabFromPath(path);
  if(modulePath)return modulePath;
  if(path==='/dashboard/module-management')return 'module-management';
  const routeTab = STATIC_ROUTE_TABS.get(path);
  if (routeTab) return routeTab;
  if (DASHBOARD_SHELL_ROUTES.has(path)) {
    const hashTab = window.location.hash.replace('#', '');
    if (isKnownAdminTab(hashTab)) return hashTab;
  }
  if (path.startsWith('/dashboard/business-reports/')) return 'business-report:unavailable';
  return 'dashboard';
}

export function AdminApp({ portal, session, onLogout, onSessionExpired, onSessionUpdate }: { portal: 'admin' | 'reports'; session: AdminUserSession; onLogout: () => void; onSessionExpired: () => void; onSessionUpdate?: (session: AdminUserSession) => void }) {
  const modules=useModuleWorkspace(session,portal);
  const [activeTab, setActiveTab] = useState<AdminTab>(requestedAdminTabFromLocation);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => isWideWorkspaceTab(requestedAdminTabFromLocation()));
  const [darkMode, setDarkMode] = useState(() => getAdminTheme() === 'dark');
  const [centers, setCenters] = useState<string[]>([]);
  const [centerFilter, setCenterFilter] = useState('ALL');
  const [daysFilter, setDaysFilter] = useState('CURRENT_MONTH');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [questionWiseDaysFilter, setQuestionWiseDaysFilter] = useState('CURRENT_MONTH');
  const [questionWiseCustomFromDate, setQuestionWiseCustomFromDate] = useState('');
  const [questionWiseCustomToDate, setQuestionWiseCustomToDate] = useState('');
  const [journeyFilter, setJourneyFilter] = useState('ALL');
  const [bucketFilter, setBucketFilter] = useState('ALL');
  const [dashboard, setDashboard] = useState<Awaited<ReturnType<typeof getDashboard>> | null>(null);
  const [dashboardLastUpdated, setDashboardLastUpdated] = useState<Date | null>(null);
  const [dashboardAutoRefresh, setDashboardAutoRefresh] = useState(false);
  const [benchmark, setBenchmark] = useState<Awaited<ReturnType<typeof getBenchmark>>>([]);
  const [feedbackRows, setFeedbackRows] = useState<Awaited<ReturnType<typeof getFeedback>>>([]);
  const [feedbackPagination, setFeedbackPagination] = useState<FeedbackPagination>({ page: 1, limit: '50', pageSize: 50, totalRows: 0, totalPages: 1, hasPreviousPage: false, hasNextPage: false });
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackPageSize, setFeedbackPageSize] = useState('50');
  const [feedbackDateRange, setFeedbackDateRange] = useState('ALL');
  const [feedbackCustomFromDate, setFeedbackCustomFromDate] = useState('');
  const [feedbackCustomToDate, setFeedbackCustomToDate] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [questionWise, setQuestionWise] = useState<Awaited<ReturnType<typeof getQuestionWise>> | null>(null);
  const [summaryRows, setSummaryRows] = useState<Awaited<ReturnType<typeof getRegistrationSummary>>>([]);
  const [summaryMonth, setSummaryMonth] = useState(currentMonth());
  const [summaryCenter, setSummaryCenter] = useState('ALL');
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [detailedSummaryRows, setDetailedSummaryRows] = useState<Awaited<ReturnType<typeof getDetailedMonthlySummary>>>([]);
  const [detailedSummaryMonth, setDetailedSummaryMonth] = useState(currentMonth());
  const [detailedSummarySearch, setDetailedSummarySearch] = useState('');
  const [detailedSummaryCenter, setDetailedSummaryCenter] = useState('ALL');
  const [detailedSummaryCategory, setDetailedSummaryCategory] = useState('ALL');
  const [detailedSummaryAppointmentStatus, setDetailedSummaryAppointmentStatus] = useState('ALL');
  const [detailedSummaryFeedbackStatus, setDetailedSummaryFeedbackStatus] = useState('ALL');
  const [detailedSummaryLoaded, setDetailedSummaryLoaded] = useState(false);
  const [staffRows, setStaffRows] = useState<Awaited<ReturnType<typeof getTeamMembers>>>([]);
  const [userRows, setUserRows] = useState<Awaited<ReturnType<typeof getUsers>>>([]);
  const [targetAccessOptions, setTargetAccessOptions] = useState<Awaited<ReturnType<typeof getBusinessReportTargetAccessOptions>>>({ businessDomains: [], accountableTeams: [] });
  const [questionRows, setQuestionRows] = useState<Awaited<ReturnType<typeof getQuestions>>>([]);
  const [roleRows, setRoleRows] = useState<AccessRole[]>([]);
  const [assignableRoleRows, setAssignableRoleRows] = useState<AccessRole[]>([]);
  const [permissionRows, setPermissionRows] = useState<AccessPermission[]>([]);
  const [integrationSettings, setIntegrationSettings] = useState<Awaited<ReturnType<typeof getIntegrationSettings>>>([]);
  const [generalSettings, setGeneralSettings] = useState<Awaited<ReturnType<typeof getGeneralSettings>> | null>(null);
  const [businessReports, setBusinessReports] = useState<BusinessReportNavigationItem[]>([]);
  const [businessReportsLoaded, setBusinessReportsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Processing your request...');
  const [topBarActions, setTopBarActions] = useState<ReactNode>(null);
  const [modal, setModal] = useState<{ message: string; type: AdminModalVariant; title?: string } | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);
  const [passwordChange, setPasswordChange] = useState<PasswordChangeState>(initialPasswordChangeState);
  const [adminOtpVerified, setAdminOtpVerified] = useState(false);
  const dashboardFilterRef = useRef({
    center: centerFilter,
    days: daysFilter,
    customFromDate,
    customToDate,
    journey: journeyFilter,
  });
  const normalizeDashboardExportFilters = useCallback((filters?: DashboardExportFilters) => ({
    ...dashboardFilterRef.current,
    ...(filters || {}),
  }), []);

  const user = session?.user;
  const hasPermission = useCallback((permission: string) => Boolean(user?.isRootAdmin || userHasPermission(user, permission)), [user]);
  const hasAnyPermission = useCallback((permissions: string[]) => permissions.some(permission => hasPermission(permission)), [hasPermission]);
  const dashboardExportDisabled = loading || !dashboard || !hasPermission('dashboard.export');
  const isAdmin = hasAnyPermission(['staff_management.read', 'user_accounts.read', 'survey_questions.read', 'role_management.read', 'general_settings.read', 'integration_settings.read']) || (portal==='admin' && hasPermission('module_management.view'));
  const token = session?.token;
  const businessReportTabs = useMemo<AdminTab[]>(() => businessReports.map((report) => `business-report:${report.key}` as AdminTab), [businessReports]);

  useEffect(() => {
    let title = viewTitles[activeTab as StaticAdminTab | 'module-management'];
    if (activeTab.startsWith('business-report:')) {
      const key = activeTab.slice('business-report:'.length);
      title = businessReports.find((report) => report.key === key)?.name || 'Business Report';
    } else if (activeTab.startsWith('module:')) {
      title = modules.items.find((item) => moduleTab(item) === activeTab)?.label || 'Platform Module';
    }
    document.title = `${title || (portal === 'admin' ? 'Administration' : 'Centre Portal')} | ${APP_PLATFORM_NAME}`;
  }, [activeTab, businessReports, modules.items, portal]);
  const visibleTabs = useMemo<AdminTab[]>(() => {
    const tabs: AdminTab[] = [];
    if (hasPermission('dashboard.read')) tabs.push('dashboard');
    if (hasPermission('clinical_dashboard.read')) tabs.push('clinical-dashboard');
    if (hasPermission('business_reports.view')) tabs.push('business-dashboard');
    if (hasPermission('doctor_report.read')) tabs.push('doctor-report');
    if (hasPermission('daily_clinical_report.read')) tabs.push('daily-clinical-report');
    if (hasPermission('doctor_report.read')) tabs.push('doctor-ivf-performance');
    if (hasPermission('doctor_report.read')) tabs.push('doctor-kpi-report');
    if (hasPermission('business_reports.view')) tabs.push('fcdr-report', 'marketing-report', 'btl-report');
    if (hasPermission('business_reports.waterfall.view')) tabs.push('waterfall-report');
    if (hasPermission('business_reports.manage')) tabs.push('business-report-administration', 'business-report-sources');
    if (hasPermission('business_reports.ownership_governance.manage')) tabs.push('business-governance-ownership');
    if (hasAnyPermission(['business_reports.target_governance.manage', 'business_reports.targets.view'])) tabs.push('business-targets-workspace');
    if (hasAnyPermission(['business_reports.validation.view', 'business_reports.manual_revenue.view', 'business_reports.manual_revenue.manage', 'business_reports.approve_manual_entries', 'business_reports.manage', 'business_reports.import', 'business_reports.reconciliation.view'])) tabs.push('business-revenue-operations');
    if (hasPermission('business_reports.operations.view')) tabs.push('business-operational-monitoring');
    if (hasPermission('business_reports.integrity.view')) tabs.push('business-data-integrity');
    if (hasPermission('feedback_tracker.read')) tabs.push('feedback-tracker');
    if (hasAnyPermission(['offer_campaigns.view', 'offer_campaigns.enroll'])) tabs.push('offer-campaigns');
    if (hasPermission('feedback_responses.read')) tabs.push('feedback');
    if (hasPermission('question_wise_response.read')) tabs.push('question-wise');
    if (hasPermission('monthly_summary.read')) tabs.push('summary');
    if (hasPermission('detailed_summary.read')) tabs.push('detailed-summary');
    if (hasPermission('staff_management.read')) tabs.push('staff');
    if (hasPermission('user_accounts.read')) tabs.push('users');
    if (hasPermission('survey_questions.read')) tabs.push('questions');
    if (hasPermission('role_management.manage')) tabs.push('roles');
    if (hasPermission('general_settings.read')) tabs.push('general-settings');
    if (hasPermission('integration_settings.read')) tabs.push('settings');
    if (hasPermission('business_reports.view')) tabs.push(...businessReportTabs);
    if(portal==='admin'&&hasPermission('module_management.view'))tabs.push('module-management');
    tabs.push(...modules.items.map(moduleTab));
    return tabs.length ? tabs : ['dashboard'];
  }, [businessReportTabs, hasAnyPermission, hasPermission, modules.items, portal]);
  const visibleCenters = useMemo(() => {
    const centerAccess = user?.centerAccess ?? [];
    if (user?.is_superadmin || user?.isRootAdmin || centerAccess.includes('ALL')) return centers;
    const allowedCenters = centerAccess.length > 0 ? centerAccess : user?.center_name ? [user.center_name] : [];
    return centers.filter(center => allowedCenters.includes(center));
  }, [centers, user?.centerAccess, user?.center_name, user?.isRootAdmin, user?.is_superadmin]);
  const sidebarDisplayName = displayNameFallback(user);
  const sidebarGreetingName = firstNameForGreeting(user);
  const sidebarRoleLabel = user?.role_name || labelFromRoleKey(String(user?.role || 'REPORTS'));
  const sidebarRoleColor = user?.role_color_text || user?.role_color_hex || 'var(--primary)';
  const businessReportCenterAccess = useMemo(
    () => user?.centerAccess?.length
      ? user.centerAccess.filter((value) => value !== 'ALL')
      : user?.center_name && user.center_name !== 'Management'
        ? [user.center_name]
        : [],
    [user?.centerAccess, user?.center_name],
  );
  const businessReportAllCenterAccess = Boolean(
    user?.is_superadmin || user?.isRootAdmin || user?.centerAccess?.includes('ALL'),
  );

  useEffect(() => {
    let active = true;
    if (!hasPermission('business_reports.view')) {
      setBusinessReports([]);
      setBusinessReportsLoaded(true);
      return undefined;
    }
    setBusinessReportsLoaded(false);
    void getBusinessReportNavigation(token)
      .then((reports) => {
        if (active) {
          setBusinessReports(reports);
          setBusinessReportsLoaded(true);
        }
      })
      .catch(() => {
        if (active) {
          setBusinessReports([]);
          setBusinessReportsLoaded(true);
        }
      });
    return () => {
      active = false;
    };
  }, [hasPermission, token]);

  const handleDaysFilterChange = useCallback((value: string) => {
    setDaysFilter(value);
    if (value === 'CUSTOM') {
      const currentMonth = currentMonthDateInputs();
      setCustomFromDate(previous => previous || currentMonth.from);
      setCustomToDate(previous => previous || currentMonth.to);
      return;
    }
    setCustomFromDate('');
    setCustomToDate('');
  }, []);

  const handleQuestionWiseDaysFilterChange = useCallback((value: string) => {
    setQuestionWiseDaysFilter(value);
    if (value === 'CUSTOM') {
      const currentMonth = currentMonthDateInputs();
      setQuestionWiseCustomFromDate(previous => previous || currentMonth.from);
      setQuestionWiseCustomToDate(previous => previous || currentMonth.to);
      return;
    }
    setQuestionWiseCustomFromDate('');
    setQuestionWiseCustomToDate('');
  }, []);

  useEffect(() => {
    dashboardFilterRef.current = {
      center: centerFilter,
      days: daysFilter,
      customFromDate,
      customToDate,
      journey: journeyFilter,
    };
  }, [centerFilter, customFromDate, customToDate, daysFilter, journeyFilter]);

  const notify = useCallback((message: string, type: AdminModalVariant = 'error', title?: string) => {
    setModal({ message, type, title });
  }, []);

  const notifyFeedbackTracker = useCallback((message: string, type: AdminModalVariant = 'success') => {
    setModal({ message, type });
  }, []);

  const runAction = useCallback(async (action: () => Promise<void>, success?: string, message = 'Processing your request...', options?: { silent?: boolean; rethrow?: boolean }) => {
    if (!options?.silent) {
      setLoadingMessage(message);
      setLoading(true);
    }
    try {
      await action();
      if (success) notify(success, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Request failed', 'error');
      if (options?.rethrow) throw error;
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, [notify]);

  const openPasswordChange = useCallback((forced = false) => {
    setPasswordChange({
      ...initialPasswordChangeState,
      open: true,
      forced,
      error: forced ? 'Reset your password before continuing.' : '',
    });
  }, []);

  const closePasswordChange = useCallback(() => {
    setPasswordChange(previous => previous.forced ? previous : initialPasswordChangeState);
  }, []);

  const updatePasswordChange = useCallback((update: Partial<PasswordChangeState>) => {
    setPasswordChange(previous => ({ ...previous, ...update }));
  }, []);

  const requestPasswordChangeOtp = useCallback(async () => {
    const newPassword = passwordChange.newPassword.trim();
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      updatePasswordChange({ error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.` });
      return;
    }
    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      updatePasswordChange({ error: 'New password and confirm password do not match.' });
      return;
    }
    updatePasswordChange({ sending: true, error: '', message: '' });
    try {
      const flow = passwordChange.forced ? 'first-login' : 'self-service';
      const result = await sendPasswordChangeOtp(token, flow, passwordChange.channel);
      const deliveryMessage = result.deliveryStatus === 'EMAIL_ACCEPTED'
        ? 'Verification code sent to your registered email.'
        : result.deliveryStatus === 'WHATSAPP_ACCEPTED'
        ? 'WhatsApp accepted the OTP request. Delivery confirmation is pending.'
        : 'WhatsApp was unavailable; the OTP was accepted by SMS fallback.';
      updatePasswordChange({
        sending: false,
        step: 'otp',
        maskedPhone: result.maskedPhone,
        message: `${deliveryMessage}${result.maskedPhone ? ` Number: ${result.maskedPhone}.` : ''}${result.maskedEmail ? ` Address: ${result.maskedEmail}.` : ''}`,
      });
    } catch (error) {
      updatePasswordChange({ sending: false, error: error instanceof Error ? error.message : 'Unable to send verification code.' });
    }
  }, [passwordChange.channel, passwordChange.confirmPassword, passwordChange.forced, passwordChange.newPassword, token, updatePasswordChange]);

  const submitPasswordChange = useCallback(async () => {
    const otp = passwordChange.otp.trim();
    if (!otp) {
      updatePasswordChange({ error: 'Verification OTP is required.' });
      return;
    }
    updatePasswordChange({ submitting: true, error: '' });
    try {
      const result = await confirmPasswordChange({
        newPassword: passwordChange.newPassword,
        confirmPassword: passwordChange.confirmPassword,
        otp,
      }, token, passwordChange.forced ? 'first-login' : 'self-service');
      onSessionUpdate?.({ token: result.token ?? token, user: result.user });
      setPasswordChange(initialPasswordChangeState);
      notify('Password changed successfully.', 'success');
    } catch (error) {
      updatePasswordChange({ submitting: false, error: error instanceof Error ? error.message : 'Unable to change password.' });
    }
  }, [notify, onSessionUpdate, passwordChange.confirmPassword, passwordChange.forced, passwordChange.newPassword, passwordChange.otp, token, updatePasswordChange]);

  useEffect(() => {
    if (!user?.password_reset_required) return;
    setPasswordChange(previous => previous.open && previous.forced ? previous : {
      ...initialPasswordChangeState,
      open: true,
      forced: true,
      error: 'Reset your password before continuing.',
    });
  }, [user?.id, user?.password_reset_required]);

  const loadCenters = useCallback(async () => {
    let lastError: unknown;
    for (const delay of [0, 400, 1_200]) {
      if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
      try {
        setCenters((await getCenters(token)).map(String));
        return;
      } catch (error) {
        lastError = error;
        if (error instanceof ApiError && error.status < 500) break;
      }
    }

    const scopedFallback = [...new Set([
      ...(user?.centerAccess ?? []),
      user?.center_name,
    ].map((center) => String(center || '').trim()).filter((center) => center && center !== 'ALL'))];
    if (scopedFallback.length) setCenters((current) => current.length ? current : scopedFallback);
    console.warn('The centre list is temporarily unavailable; existing centre scope has been retained.', lastError);
  }, [token, user?.centerAccess, user?.center_name]);

  const loadDashboardData = useCallback(async (options?: { silent?: boolean }) => {
    await runAction(async () => {
      const dateRange = adminDateRangeParams(daysFilter, customFromDate, customToDate);
      const [dashboardData, benchmarkData] = await Promise.all([
        getDashboard({ token, center: centerFilter, ...dateRange, journey: journeyFilter }),
        getBenchmark(token, dateRange),
      ]);
      setDashboard(dashboardData);
      setBenchmark(Array.isArray(benchmarkData) ? benchmarkData : []);
      setDashboardLastUpdated(new Date());
    }, undefined, 'Processing your request...', options);
  }, [centerFilter, customFromDate, customToDate, daysFilter, journeyFilter, runAction, token]);

  const loadFeedbackData = useCallback(async (options?: { silent?: boolean; validateCustomDate?: boolean }) => {
    await runAction(async () => {
      const dateRange = feedbackDateRangeParams(feedbackDateRange, feedbackCustomFromDate, feedbackCustomToDate, {
        validateCustomDate: options?.validateCustomDate,
      });
      if (!dateRange) return;
      const response = await getFeedbackPage({
        token,
        feedback_center: centerFilter,
        journey: journeyFilter,
        bucket: bucketFilter,
        ...dateRange,
        page: feedbackPage,
        limit: feedbackPageSize,
        search: feedbackSearch,
      });
      setFeedbackRows(response.rows);
      setFeedbackPagination(response.pagination);
    }, undefined, 'Processing your request...', options);
  }, [bucketFilter, centerFilter, feedbackCustomFromDate, feedbackCustomToDate, feedbackDateRange, feedbackPage, feedbackPageSize, feedbackSearch, journeyFilter, runAction, token]);

  const loadQuestionWiseData = useCallback(async (options?: { silent?: boolean }) => {
    await runAction(async () => setQuestionWise(await getQuestionWise({ token, center: centerFilter, journey: journeyFilter, ...adminDateRangeParams(questionWiseDaysFilter, questionWiseCustomFromDate, questionWiseCustomToDate) })), undefined, 'Processing your request...', options);
  }, [centerFilter, journeyFilter, questionWiseCustomFromDate, questionWiseCustomToDate, questionWiseDaysFilter, runAction, token]);

  const loadSummaryData = useCallback(async (options?: { silent?: boolean }) => {
    await runAction(async () => {
      setSummaryRows(await getRegistrationSummary(token, summaryMonth, summaryCenter));
      setSummaryLoaded(true);
    }, undefined, 'Processing your request...', options);
  }, [runAction, summaryCenter, summaryMonth, token]);

  const loadDetailedSummaryData = useCallback(async (options?: { silent?: boolean }) => {
    await runAction(async () => {
      setDetailedSummaryRows(await getDetailedMonthlySummary(token, detailedSummaryMonth));
      setDetailedSummaryLoaded(true);
    }, undefined, 'Processing your request...', options);
  }, [detailedSummaryMonth, runAction, token]);

  const loadManagementData = useCallback(async () => {
    const [staff, users, questions, roles, permissions, general, settings, targetOptions] = await Promise.all([
      hasPermission('staff_management.read') ? getTeamMembers(token).catch(() => []) : Promise.resolve([]),
      hasPermission('user_accounts.read') ? getUsers(token).catch(() => []) : Promise.resolve([]),
      hasPermission('survey_questions.read') ? getQuestions(token).catch(() => []) : Promise.resolve([]),
      hasPermission('role_management.read') ? getAccessRoles(token, true).catch(() => []) : Promise.resolve([]),
      hasPermission('role_management.read') ? getAccessPermissions(token).catch(() => []) : Promise.resolve([]),
      hasPermission('general_settings.read') ? getGeneralSettings(token).catch(() => null) : Promise.resolve(null),
      hasPermission('integration_settings.read') ? getIntegrationSettings(token).catch(() => []) : Promise.resolve([]),
      hasPermission('user_accounts.read') ? getBusinessReportTargetAccessOptions(token).catch(() => ({ businessDomains: [], accountableTeams: [] })) : Promise.resolve({ businessDomains: [], accountableTeams: [] }),
    ]);
    const assignableRoles = hasPermission('user_accounts.read')
      ? await getAssignableAccessRoles(token).catch(() => [])
      : [];
    setStaffRows(staff);
    setUserRows(users);
    setQuestionRows(questions);
    setRoleRows(roles);
    setAssignableRoleRows(assignableRoles);
    setPermissionRows(permissions);
    setGeneralSettings(general);
    setIntegrationSettings(settings);
    setTargetAccessOptions(targetOptions);
  }, [hasPermission, token]);

  const showTab = useCallback((tab: AdminTab) => {
    if(tab!==activeTab && activeTab.startsWith('module:') && !modules.mayLeave())return;
    const dashboardRoute = portal === 'reports' ? REPORTS_DASHBOARD_ROUTE : ADMIN_DASHBOARD_ROUTE;
    if (!visibleTabs.includes(tab)) {
      const fallback = visibleTabs[0] || 'dashboard';
      setActiveTab(fallback);
      window.location.hash = fallback;
      return;
    }
    setActiveTab(tab);
    if(tab.startsWith('module:')){
      const page=modules.items.find(p=>moduleTab(p)===tab);
      if(page)window.history.pushState({},'',page.href);
    } else if(tab==='module-management'){
      window.history.pushState({},'','/dashboard/module-management');
    } else if (isBusinessReportTab(tab)) {
      const report = businessReports.find((item) => `business-report:${item.key}` === tab);
      if (report) window.history.pushState({}, '', portalSafeBusinessReportRoute(report.routePath));
    } else if (tab === 'clinical-dashboard') {
      window.history.pushState({}, '', portal === 'reports' ? `${dashboardRoute}#clinical-dashboard` : CLINICAL_DASHBOARD_ROUTE);
    } else if (tab === 'business-dashboard') {
      window.history.pushState({}, '', portal === 'reports' ? `${dashboardRoute}#business-dashboard` : BUSINESS_DASHBOARD_ROUTE);
    } else if (tab === 'doctor-report') {
      window.history.pushState({}, '', '/reports/clinical/doctor');
    } else if (tab === 'daily-clinical-report') {
      window.history.pushState({}, '', '/reports/clinical/daily');
    } else if (tab === 'doctor-kpi-report') {
      window.history.pushState({}, '', DOCTOR_KPI_ROUTE);
    } else if (tab === 'doctor-ivf-performance') {
      window.history.pushState({}, '', '/reports/clinical/doctor-wise-ivf');
    } else if (tab === FCDR_TAB) {
      window.history.pushState({}, '', FCDR_ROUTE);
    } else if (tab === WATERFALL_TAB) {
      window.history.pushState({}, '', WATERFALL_ROUTE);
    } else if (tab === 'marketing-report') {
      window.history.pushState({}, '', '/reports/marketing/overview');
    } else if (tab === 'btl-report') {
      window.history.pushState({}, '', '/reports/marketing/btl');
    } else if (['business-data-integrity', 'business-report-administration', 'business-report-sources', 'business-report-centers', 'business-report-financial-calendar', 'business-revenue-sources', 'business-revenue-source-definitions', 'business-revenue-category-rules', 'business-governance-ownership', 'business-targets-workspace', 'business-revenue-operations', 'business-operational-monitoring'].includes(tab)) {
      window.history.pushState(
        {},
        '',
        tab === 'business-data-integrity'
          ? businessReportRoute('data-integrity')
          : tab === 'business-report-administration'
            ? businessReportRoute('administration', 'tab=reports')
          : tab === 'business-report-centers'
            ? businessReportRoute('centre-master')
          : tab === 'business-report-financial-calendar'
            ? businessReportRoute('financial-calendar')
          : tab === 'business-report-sources'
            ? businessReportRoute('data-sources', 'tab=data-sources')
          : tab === 'business-revenue-sources'
            ? businessReportRoute('revenue-sources')
          : tab === 'business-revenue-category-rules'
            ? businessReportRoute('data-sources', 'tab=revenue-category-rules')
          : tab === 'business-revenue-source-definitions'
            ? businessReportRoute('source-onboarding')
          : tab === 'business-governance-ownership'
            ? businessReportRoute('governance-ownership')
          : tab === 'business-targets-workspace'
            ? businessReportRoute('targets')
          : tab === 'business-revenue-operations'
            ? businessReportRoute('revenue-operations')
          : tab === 'business-operational-monitoring'
            ? businessReportRoute('operations')
            : businessReportRoute('data-sources'),
      );
    } else {
      window.history.pushState({}, '', `${dashboardRoute}#${tab}`);
    }
    if (isWideWorkspaceTab(tab)) {
      setSidebarOpen(false);
      setSidebarCollapsed(true);
    } else if (window.innerWidth <= 768) setSidebarOpen(false);
  }, [businessReports, portal, visibleTabs, activeTab, modules]);

  useEffect(()=>{
    const changed=()=>{
      const target=requestedAdminTabFromLocation();
      if(activeTab.startsWith('module:')&&target!==activeTab&&!modules.mayLeave()){
        const page=modules.items.find(p=>moduleTab(p)===activeTab);if(page)window.history.pushState({},'',page.href);return;
      }
      if(target.startsWith('module:')||target==='module-management')setActiveTab(target);
      else if(visibleTabs.includes(target))setActiveTab(target);
    };
    window.addEventListener('popstate',changed);window.addEventListener('hashchange',changed);
    return()=>{window.removeEventListener('popstate',changed);window.removeEventListener('hashchange',changed);};
  },[activeTab,modules,visibleTabs]);

  useEffect(()=>{
    if(modules.loaded&&modules.items.length&&activeTab==='dashboard'&&!hasPermission('dashboard.read'))showTab(moduleTab(modules.items[0]));
  },[modules.loaded,modules.items,activeTab,hasPermission,showTab]);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(open => !open);
      return;
    }
    setSidebarCollapsed(collapsed => !collapsed);
  }, []);

  useEffect(() => {
    const path = normalizeBusinessReportPath(window.location.pathname);
    if (!REPORT_WIDE_ROUTES.has(path)) return;
    setSidebarOpen(false);
    setSidebarCollapsed(true);
  }, []);

  useEffect(() => {
    if (!isWideWorkspaceTab(activeTab)) return;
    setSidebarOpen(false);
    setSidebarCollapsed(true);
  }, [activeTab]);

  useEffect(() => {
    document.body.classList.add('admin-reporting-active');
    return () => {
      document.body.classList.remove('admin-reporting-active');
      document.body.classList.remove('dark-mode');
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    setAdminTheme(darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    void loadCenters();
    if (hasPermission('dashboard.read')) void loadDashboardData({ silent: true });
    if (isAdmin) void loadManagementData();
  }, [hasPermission, isAdmin, loadCenters, loadDashboardData, loadManagementData, session]);

  useEffect(() => {
    if (activeTab === 'dashboard' && hasPermission('dashboard.read')) void loadDashboardData({ silent: true });
    if (activeTab === 'feedback') void loadFeedbackData({ silent: true });
    if (activeTab === 'question-wise') void loadQuestionWiseData({ silent: true });
    if (activeTab === 'detailed-summary' && detailedSummaryLoaded) void loadDetailedSummaryData({ silent: true });
    if (['staff', 'users', 'questions', 'roles', 'general-settings', 'settings'].includes(activeTab)) void loadManagementData();
  }, [activeTab, detailedSummaryLoaded, hasPermission, loadDashboardData, loadDetailedSummaryData, loadFeedbackData, loadManagementData, loadQuestionWiseData]);

  useEffect(() => {
    if (activeTab !== 'feedback-tracker' && activeTab !== 'settings' && activeTab !== 'doctor-kpi-report' && activeTab !== 'business-report-administration' && activeTab !== 'business-report-sources' && activeTab !== 'business-targets-workspace' && activeTab !== 'business-revenue-operations' && activeTab !== 'business-data-integrity' && activeTab !== DAILY_REVENUE_TAB && activeTab !== 'business-report:MEDICS_REVENUE') {
      setTopBarActions(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'dashboard' || !dashboardAutoRefresh) return undefined;
    const interval = window.setInterval(() => void loadDashboardData({ silent: true }), 300000);
    return () => window.clearInterval(interval);
  }, [activeTab, dashboardAutoRefresh, loadDashboardData]);

  useEffect(() => {
    if (!DASHBOARD_SHELL_ROUTES.has(normalizeBusinessReportPath(window.location.pathname))) return;
    const currentHash = window.location.hash.replace('#', '');
    if (!isKnownAdminTab(currentHash)) return;
    if (!visibleTabs.includes(currentHash)) {
      const fallback = visibleTabs[0] || 'dashboard';
      setActiveTab(fallback);
      window.location.hash = fallback;
      return;
    }
    setActiveTab(currentHash);
  }, [visibleTabs]);

  useEffect(() => {
    const path = normalizeBusinessReportPath(window.location.pathname);
    if (path === '/dashboard/clinical') {
      setActiveTab(hasPermission('clinical_dashboard.read') ? 'clinical-dashboard' : 'business-report:unavailable');
      return;
    }
    if (path === BUSINESS_DASHBOARD_ROUTE) {
      setActiveTab(hasPermission('business_reports.view') ? 'business-dashboard' : 'business-report:unavailable');
      return;
    }
    if (path === '/reports/clinical/doctor') {
      setActiveTab(hasPermission('doctor_report.read') ? 'doctor-report' : 'business-report:unavailable');
      return;
    }
    if (path === '/reports/clinical/daily') {
      setActiveTab(hasPermission('daily_clinical_report.read') ? 'daily-clinical-report' : 'business-report:unavailable');
      return;
    }
    if (path === DOCTOR_KPI_ROUTE) {
      setActiveTab(hasPermission('doctor_report.read') ? 'doctor-kpi-report' : 'business-report:unavailable');
      return;
    }
    if (path === '/reports/clinical/doctor-wise-ivf') {
      setActiveTab(hasPermission('doctor_report.read') ? 'doctor-ivf-performance' : 'business-report:unavailable');
      return;
    }
    if (path === FCDR_ROUTE) {
      setActiveTab(hasPermission('business_reports.view') ? FCDR_TAB : 'business-report:unavailable');
      return;
    }
    if (path === WATERFALL_ROUTE) {
      setActiveTab(hasPermission('business_reports.waterfall.view') ? WATERFALL_TAB : 'business-report:unavailable');
      return;
    }
    if (path === '/reports/marketing/overview') {
      setActiveTab(hasPermission('business_reports.view') ? 'marketing-report' : 'business-report:unavailable');
      return;
    }
    if (path === '/reports/marketing/btl') {
      setActiveTab(hasPermission('business_reports.view') ? 'btl-report' : 'business-report:unavailable');
      return;
    }
    if (path === DAILY_REVENUE_ROUTE) {
      setActiveTab(hasPermission('business_reports.daily_revenue.view') ? DAILY_REVENUE_TAB : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/administration') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-administration' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/data-sources') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-sources' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/centre-master') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-administration' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/financial-calendar') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-administration' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/revenue-sources') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-sources' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/revenue-category-rules') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-sources' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/source-onboarding') {
      setActiveTab(hasPermission('business_reports.manage') ? 'business-report-sources' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/revenue-operations' || path === '/dashboard/business-reports/validation') {
      setActiveTab(hasAnyPermission(['business_reports.validation.view', 'business_reports.manual_revenue.view', 'business_reports.manual_revenue.manage', 'business_reports.approve_manual_entries', 'business_reports.manage', 'business_reports.import', 'business_reports.reconciliation.view']) ? 'business-revenue-operations' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/manual-revenue') {
      setActiveTab(hasAnyPermission(['business_reports.manual_revenue.view', 'business_reports.manual_revenue.manage', 'business_reports.approve_manual_entries', 'business_reports.manage']) ? 'business-revenue-operations' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/governance-ownership') {
      setActiveTab(hasPermission('business_reports.ownership_governance.manage') ? 'business-governance-ownership' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/target-governance') {
      setActiveTab(hasPermission('business_reports.target_governance.manage') ? 'business-targets-workspace' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/monthly-targets') {
      setActiveTab(hasPermission('business_reports.targets.view') ? 'business-targets-workspace' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/targets') {
      setActiveTab(hasAnyPermission(['business_reports.target_governance.manage', 'business_reports.targets.view']) ? 'business-targets-workspace' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/file-imports') {
      setActiveTab(hasPermission('business_reports.import') ? 'business-revenue-operations' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/reconciliation') {
      setActiveTab(hasPermission('business_reports.reconciliation.view') ? 'business-revenue-operations' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/operations') {
      setActiveTab(hasPermission('business_reports.operations.view') ? 'business-operational-monitoring' : 'business-report:unavailable');
      return;
    }
    if (path === '/dashboard/business-reports/data-integrity') {
      setActiveTab(hasPermission('business_reports.integrity.view') ? 'business-data-integrity' : 'business-report:unavailable');
      return;
    }
    if (!businessReportsLoaded) return;
    if (!path.startsWith('/dashboard/business-reports/')) return;
    const report = businessReports.find((item) => normalizeBusinessReportPath(item.routePath) === path);
    if (report) {
      setActiveTab(`business-report:${report.key}`);
      return;
    }
    setActiveTab('business-report:unavailable');
  }, [businessReports, businessReportsLoaded, hasAnyPermission, hasPermission]);

  useEffect(() => {
    const allowedValues = new Set(['ALL', ...visibleCenters]);
    if (!allowedValues.has(centerFilter)) setCenterFilter('ALL');
    if (!allowedValues.has(summaryCenter)) {
      setSummaryCenter('ALL');
      setSummaryLoaded(false);
      setSummaryRows([]);
    }
    if (!allowedValues.has(detailedSummaryCenter)) setDetailedSummaryCenter('ALL');
  }, [centerFilter, detailedSummaryCenter, summaryCenter, visibleCenters]);

  useEffect(() => {
    setFeedbackPage(1);
  }, [centerFilter, journeyFilter]);

  useEffect(() => {
    const handleSessionExpired = () => {
      onSessionExpired();
    };
    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [onSessionExpired]);

  const confirm = useCallback((message: string, action: () => Promise<void>) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
  }, []);

  const setFeedbackFilterPageReset = useCallback((update: () => void) => {
    setFeedbackPage(1);
    update();
  }, []);

  const actions = useMemo<AdminActions>(() => ({
    setCenterFilter,
    setDaysFilter: handleDaysFilterChange,
    setCustomFromDate,
    setCustomToDate,
    setJourneyFilter,
    setBucketFilter: value => setFeedbackFilterPageReset(() => setBucketFilter(value)),
    setFeedbackDateRange: value => setFeedbackFilterPageReset(() => setFeedbackDateRange(value)),
    setFeedbackCustomFromDate: value => setFeedbackFilterPageReset(() => setFeedbackCustomFromDate(value)),
    setFeedbackCustomToDate: value => setFeedbackFilterPageReset(() => setFeedbackCustomToDate(value)),
    setFeedbackSearch: value => setFeedbackFilterPageReset(() => setFeedbackSearch(value)),
    setFeedbackPage,
    setFeedbackPageSize: value => setFeedbackFilterPageReset(() => setFeedbackPageSize(value)),
    setSummaryMonth: value => {
      setSummaryMonth(value);
      setSummaryLoaded(false);
      setSummaryRows([]);
    },
    setSummaryCenter: value => {
      setSummaryCenter(value);
      setSummaryLoaded(false);
      setSummaryRows([]);
    },
    setDetailedSummaryMonth: value => {
      setDetailedSummaryMonth(value);
      setDetailedSummaryLoaded(false);
      setDetailedSummaryRows([]);
    },
    setDetailedSummarySearch,
    setDetailedSummaryCenter,
    setDetailedSummaryCategory,
    setDetailedSummaryAppointmentStatus,
    setDetailedSummaryFeedbackStatus,
    notify: message => notify(message, 'info'),
    refreshDashboard: () => void loadDashboardData(),
    refreshFeedback: () => void loadFeedbackData({ validateCustomDate: true }),
    refreshQuestionWise: () => void loadQuestionWiseData(),
    refreshSummary: () => void loadSummaryData(),
    refreshDetailedSummary: () => void loadDetailedSummaryData(),
    createStaff: payload => runAction(async () => { await createTeamMember(payload, token); await loadManagementData(); }, 'Staff member saved'),
    updateStaff: (id, payload) => runAction(async () => { await updateTeamMember(id, payload, token); await loadManagementData(); }, 'Staff member updated'),
    deleteStaff: id => new Promise(resolve => confirm('Delete this staff member?', async () => { await runAction(async () => { await deleteTeamMember(id, token); await loadManagementData(); }, 'Staff member deleted'); resolve(); })),
    createUser: payload => runAction(async () => { const result = await createUserRequest(payload as AdminUserPayload, token); await loadManagementData(); if (!result.notification.sent) notify(`User created, but welcome email was not delivered. ${result.notification.error || 'Check the email provider and resend.'}`, 'warning', 'Notification failed'); else notify('User created and welcome email sent.', 'success'); }),
    updateUser: (id, payload) => runAction(async () => { await updateUserRequest(id, payload as AdminUserPayload, token); await loadManagementData(); }, 'User updated'),
    deleteUser: id => new Promise(resolve => confirm('Delete this user?', async () => { await runAction(async () => { await deleteUserRequest(id, token); await loadManagementData(); }, 'User deleted'); resolve(); })),
    forceLogoutUser: id => runAction(async () => { await forceLogoutUserRequest(id, token); await loadManagementData(); }, 'User logged out'),
    unlockUserLogin: async id => {
      await unlockUserLoginRequest(id, token);
      await loadManagementData();
      notify('Login lock cleared', 'success');
    },
    resendUserWelcome: async (id, temporaryPassword) => {
      const result = await resendUserWelcomeRequest(id, temporaryPassword, token);
      if (!result.notification.sent) throw new Error(result.notification.error || 'Welcome email was not delivered.');
      await loadManagementData(); notify('Welcome email sent successfully. The new temporary password is active.', 'success');
    },
    forceLogoutUsersByRole: roleKeys => runAction(async () => {
      const result = await forceLogoutUsersByRoleRequest(roleKeys, token);
      await loadManagementData();
      notify(`${result.affected} online user${result.affected === 1 ? '' : 's'} logged out.`, 'success');
    }),
    createRole: payload => runAction(async () => { await createAccessRole(payload, token); await loadManagementData(); }, 'Role created'),
    updateRole: (key, payload) => runAction(async () => { await updateAccessRole(key, payload, token); await loadManagementData(); }, 'Role updated'),
    deleteRole: key => new Promise(resolve => confirm('Delete this custom role? Existing users must be reassigned before production use.', async () => { await runAction(async () => { await deleteAccessRole(key, token); await loadManagementData(); }, 'Role deleted'); resolve(); })),
    updateIntegrationSetting: (key, payload) => runAction(async () => { await updateIntegrationSettingRequest(key, payload, token); await loadManagementData(); }, 'Integration setting saved'),
    deleteIntegrationSetting: key => runAction(async () => { await deleteIntegrationSettingRequest(key, token); await loadManagementData(); }, 'Integration setting deleted'),
    getExotelWhatsappTemplates: () => getExotelWhatsappTemplates(token),
    syncExotelWhatsappTemplate: (key, templateName) => runAction(async () => { await syncExotelWhatsappTemplateRequest(key, templateName, token); await loadManagementData(); }, 'Template synced from Exotel'),
    sendAdminOtp: () => runAction(async () => { await sendAdminVerificationOtp(token); }, 'Admin OTP sent'),
    verifyAdminOtp: async otp => {
      setLoadingMessage('Processing your request...');
      setLoading(true);
      try {
        const result = await verifyAdminVerificationOtp(otp, token);
        if (!result.success) throw new Error('OTP verification failed.');
        setAdminOtpVerified(true);
        notify('Admin OTP verified', 'success');
      } catch (error) {
        setAdminOtpVerified(false);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    createQuestion: payload => runAction(async () => { await createQuestionRequest(payload, token); await loadManagementData(); }, 'Question saved'),
    updateQuestion: (id, payload) => runAction(async () => { await updateQuestionRequest(id, payload, token); await loadManagementData(); }, 'Question updated'),
    deleteQuestion: id => new Promise(resolve => confirm('Delete this question?', async () => { await runAction(async () => { await deleteQuestionRequest(id, token); await loadManagementData(); }, 'Question deleted'); resolve(); })),
    deleteFeedback: id => new Promise(resolve => confirm('Are you sure you want to delete this response? This action cannot be undone.', async () => { await runAction(async () => { await deleteFeedbackRequest(id, token); await loadFeedbackData(); }, 'Response deleted successfully.'); resolve(); })),
    exportFeedback: (scope = 'full-result') => runAction(async () => {
      if (!hasPermission('feedback_responses.export')) throw new Error('You do not have permission to export Feedback Responses.');
      const dateRange = feedbackDateRangeParams(feedbackDateRange, feedbackCustomFromDate, feedbackCustomToDate, {
        validateCustomDate: true,
      });
      if (!dateRange) return;
      const rows = scope === 'current-page'
        ? feedbackRows
        : await getFeedback({
          token,
          feedback_center: centerFilter,
          journey: journeyFilter,
          bucket: bucketFilter,
          ...dateRange,
          search: feedbackSearch,
        });
      const exportRows = feedbackExportRows(rows, questionRows);
      if (!exportRows.length) {
        throw new Error('No feedback responses are available for export with the selected filters.');
      }
      const scopeLabel = scope === 'current-page' ? 'Current_Page' : 'Full_Result';
      exportFeedbackRows(exportRows, `Ferty9_NPS_Feedback_${scopeLabel}_${new Date().toISOString().split('T')[0]}.xlsx`);
    }, undefined, 'Processing your request...'),
    exportJourneyDetailedMetrics: () => runAction(async () => {
      if (!hasPermission('dashboard.export')) throw new Error('You do not have permission to export Dashboard reports.');
      const rows = await getFeedback({
        token,
        feedback_center: centerFilter,
        journey: journeyFilter,
        bucket: 'ALL',
        ...adminDateRangeParams(daysFilter, customFromDate, customToDate),
      });
      const exportRows = feedbackExportRows(rows, questionRows);
      if (!exportRows.length) {
        throw new Error('No Journey Detailed Metrics rows are available for the selected dashboard filters.');
      }
      exportFeedbackRows(exportRows, `Ferty9_Journey_Detailed_Metrics_${new Date().toISOString().split('T')[0]}.xlsx`);
    }, undefined, 'Processing your request...'),
    exportQuestionWise: () => runAction(async () => {
      if (!hasPermission('question_wise_response.export')) throw new Error('You do not have permission to export Question Responses.');
      exportQuestionWiseRows(toExportRows(questionWiseRows(questionWise).map(row => {
      const source = row as Record<string, unknown>;
      return {
        Category: source.category ?? '-',
        Question: source.question_text ?? source.question ?? source.text ?? '-',
        Responses: source.total ?? source.responses ?? source.responseCount ?? 0,
        Average: source.average ?? source.avg ?? source.avgRating ?? '-',
        Promoters: source.promoters ?? 0,
        Passives: source.passives ?? 0,
        Detractors: source.detractors ?? 0,
        NPS: source.nps ?? source.npsScore ?? '-',
      };
      })));
    }, undefined, 'Processing your request...'),
    exportSummary: table => runAction(async () => {
      if (!hasPermission('monthly_summary.export')) throw new Error('You do not have permission to export Monthly Summary.');
      if (!table) throw new Error('Monthly summary table not found.');
      await exportMonthlySummaryTable(table, summaryMonth);
    }, undefined, 'Processing your request...'),
    exportDetailedSummary: table => runAction(async () => {
      if (!hasPermission('detailed_summary.export')) throw new Error('You do not have permission to export Detailed Summary.');
      if (!detailedSummaryLoaded) throw new Error('Please select a month and click "Refresh Data" before exporting detailed summary.');
      if (!table) throw new Error('Detailed monthly summary table not found.');
      await exportDetailedMonthlySummaryTable(table, detailedSummaryMonth);
    }, undefined, 'Processing your request...'),
    exportDashboardPdf: filters => runAction(async () => {
      if (!hasPermission('dashboard.export')) throw new Error('You do not have permission to export the NPS Dashboard.');
      const selectedFilters = normalizeDashboardExportFilters(filters);
      const pdf = await exportDashboardPdfReport({
        token,
        center: selectedFilters.center,
        ...adminDateRangeParams(selectedFilters.days, selectedFilters.customFromDate, selectedFilters.customToDate),
        journey: selectedFilters.journey,
      });
      downloadBlob(pdf, pdf.name || 'Ferty9_Dashboard_Report.pdf');
    }, undefined, 'Generating dashboard PDF...'),
    exportSummaryPdf: element => runAction(async () => {
      if (!hasPermission('monthly_summary.export')) throw new Error('You do not have permission to export Monthly Summary.');
      if (!summaryLoaded) throw new Error('Please select a month and click "Refresh Summary" to view performance data before exporting PDF.');
      if (!element) throw new Error('Monthly summary content not found.');
      await exportElementAsPdf(element, summaryPdfFileName(summaryMonth), {
        prepareClone: clone => prepareSummaryPdfClone(clone, readableMonth(summaryMonth)),
        orientation: 'landscape',
        widthPx: 1400,
      });
    },
    undefined,
    'Processing your request...'),
    printQuestionWise: () => {
      if (!hasPermission('question_wise_response.export')) {
        notify('You do not have permission to export Question Responses.', 'error');
        return;
      }
      window.print();
    },
  }), [bucketFilter, centerFilter, confirm, customFromDate, customToDate, daysFilter, detailedSummaryLoaded, detailedSummaryMonth, feedbackCustomFromDate, feedbackCustomToDate, feedbackDateRange, feedbackRows, feedbackSearch, handleDaysFilterChange, hasPermission, journeyFilter, loadDashboardData, loadDetailedSummaryData, loadFeedbackData, loadManagementData, loadQuestionWiseData, loadSummaryData, normalizeDashboardExportFilters, notify, questionRows, questionWise, runAction, setFeedbackFilterPageReset, summaryCenter, summaryLoaded, summaryMonth, token]);

  const questionWiseActions = useMemo<AdminActions>(() => ({
    ...actions,
    setDaysFilter: handleQuestionWiseDaysFilterChange,
    setCustomFromDate: setQuestionWiseCustomFromDate,
    setCustomToDate: setQuestionWiseCustomToDate,
  }), [actions, handleQuestionWiseDaysFilterChange]);

  return (
    <>
      <AdminShell
        activeTab={activeTab}
        isAdmin={isAdmin}
        visibleTabs={visibleTabs}
        modulePages={modules.items}
        isOpen={sidebarOpen}
        isCollapsed={sidebarCollapsed}
        darkMode={darkMode}
        greetingLabel={`Hi, ${sidebarGreetingName}`}
        userName={sidebarDisplayName}
        roleLabel={sidebarRoleLabel}
        roleColor={sidebarRoleColor}
        businessReports={businessReports}
        canViewBusinessReports={hasAnyPermission(['business_reports.view', 'business_reports.waterfall.view', 'business_reports.manage', 'business_reports.import', 'business_reports.manual_revenue.view', 'business_reports.manual_revenue.manage', 'business_reports.approve_manual_entries', 'business_reports.validation.view', 'business_reports.targets.view', 'business_reports.target_governance.manage', 'business_reports.ownership_governance.manage', 'business_reports.reconciliation.view', 'business_reports.operations.view', 'business_reports.integrity.view'])}
        canManageBusinessReports={hasPermission('business_reports.manage')}
        canImportBusinessReports={hasPermission('business_reports.import')}
        businessReportsLoading={hasPermission('business_reports.view') && !businessReportsLoaded}
        activeTabTitle={activeTab==='module-management'?'Module Management':activeTab.startsWith('module:')?(modules.items.find(p=>moduleTab(p)===activeTab)?.label||'Module page'):isBusinessReportTab(activeTab) ? businessReports.find((report) => `business-report:${report.key}` === activeTab)?.name : undefined}
        topBarActions={topBarActions}
        onTab={showTab}
        onLogout={()=>{modules.store.clear();window.dispatchEvent(new Event(MODULE_SESSION_CLEAR));onLogout();}}
        onSidebarToggle={toggleSidebar}
        onThemeToggle={() => setDarkMode(value => !value)}
        onThemeChange={theme => setDarkMode(theme === 'dark')}
        onChangePassword={() => openPasswordChange(false)}
      >
        {loading && <Loader message={loadingMessage} onClose={() => setLoading(false)} />}
        <ModuleExtensionSurface active surface="COMMAND_PALETTE" token={token} portal={portal} store={modules.store}/>
        <ModuleExtensionSurface active={!activeTab.startsWith('module:')} surface="RECORD_CONTEXT" contextType={activeTab.replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,48)} token={token} portal={portal} store={modules.store}/>
        <ModulePageHost tab={activeTab} items={modules.items} store={modules.store} token={token} portal={portal} loaded={modules.loaded} error={modules.error}/>
        <ModuleManagementEntry active={activeTab==='module-management'} allowed={portal==='admin'&&hasPermission('module_management.view')} token={token}/>
        <DashboardTab active={activeTab === 'dashboard'} centers={visibleCenters} center={centerFilter} days={daysFilter} customFromDate={customFromDate} customToDate={customToDate} journey={journeyFilter} dashboard={dashboard} benchmark={benchmark} lastUpdated={dashboardLastUpdated} autoRefresh={dashboardAutoRefresh} exportDisabled={dashboardExportDisabled} onCenter={setCenterFilter} onDays={handleDaysFilterChange} onCustomFromDate={setCustomFromDate} onCustomToDate={setCustomToDate} onJourney={setJourneyFilter} onRefresh={loadDashboardData} onAutoRefreshChange={setDashboardAutoRefresh} actions={actions} />
        <ModuleExtensionSurface active={activeTab === 'dashboard'} surface="DASHBOARD" token={token} portal={portal} store={modules.store}/>
        <ClinicalDashboardTab
          active={activeTab === 'clinical-dashboard'}
          token={token}
          canExport={hasPermission('clinical_dashboard.export')}
          notify={notify}
        />
        <BusinessDashboardTab
          active={activeTab === 'business-dashboard'}
          token={token}
          canViewAncPatients={hasPermission('business_reports.source_sensitive.view')}
          canExportAncPatients={hasPermission('business_reports.export') && hasPermission('business_reports.source_sensitive.view') && hasPermission('business_reports.source_sensitive.export')}
          notify={notify}
        />
        <DoctorReportTab
          active={activeTab === 'doctor-report'}
          token={token}
          canExport={hasPermission('doctor_report.export')}
          notify={notify}
        />
        <DailyClinicalReportTab
          active={activeTab === 'daily-clinical-report'}
          centers={visibleCenters}
          token={token}
          canExport={hasPermission('daily_clinical_report.export')}
          notify={notify}
        />
        <DoctorKpiReportTab active={activeTab === 'doctor-kpi-report'} token={token} canExport={hasPermission('doctor_report.export')} onTopBarActionsChange={setTopBarActions} />
        <DoctorIvfPerformanceTab active={activeTab === 'doctor-ivf-performance'} centers={visibleCenters} token={token} canExport={hasPermission('doctor_report.export')} />
        <FcdrReportTab active={activeTab === FCDR_TAB} centers={visibleCenters} token={token} canExport={hasPermission('business_reports.export') && hasPermission('business_reports.source_sensitive.view') && hasPermission('business_reports.source_sensitive.export')} />
        <WaterfallReportTab active={activeTab === WATERFALL_TAB} centers={visibleCenters} token={token} canExport={hasPermission('business_reports.waterfall.export') && hasPermission('business_reports.waterfall.source_sensitive.view') && hasPermission('business_reports.waterfall.source_sensitive.export')} canViewSensitive={hasPermission('business_reports.waterfall.source_sensitive.view')} canExportSensitive={hasPermission('business_reports.waterfall.export') && hasPermission('business_reports.waterfall.source_sensitive.view') && hasPermission('business_reports.waterfall.source_sensitive.export')} />
        <MarketingReportTab active={activeTab === 'marketing-report'} centers={visibleCenters} token={token} canExport={hasPermission('business_reports.export') && hasPermission('business_reports.source_sensitive.view') && hasPermission('business_reports.source_sensitive.export')} />
        <BtlReportTab active={activeTab === 'btl-report'} centers={visibleCenters} token={token} canExport={hasPermission('business_reports.export')} />
        <BusinessReportAdministrationWorkspace active={activeTab === 'business-report-administration'} token={token} canExport={hasPermission('business_reports.export')} onTopBarActionsChange={setTopBarActions} />
        <BusinessReportDataSourcesWorkspace active={activeTab === 'business-report-sources'} token={token} onTopBarActionsChange={setTopBarActions} />
        <BusinessGovernanceOwnershipTab active={activeTab === 'business-governance-ownership'} token={token} userName={String(user?.username || user?.id || '')} />
        <BusinessTargetsWorkspace
          active={activeTab === 'business-targets-workspace'}
          token={token}
          userName={String(user?.username || user?.id || '')}
          centerAccess={businessReportCenterAccess}
          allCenterAccess={businessReportAllCenterAccess}
          canManageGovernance={hasPermission('business_reports.target_governance.manage')}
          canViewTargets={hasPermission('business_reports.targets.view')}
          canCreate={hasPermission('business_reports.targets.create')}
          canUpdate={hasPermission('business_reports.targets.update')}
          canExport={hasPermission('business_reports.targets.export')}
          canRequestOverride={hasPermission('business_reports.landing_override.request')}
          canApproveOverride={hasPermission('business_reports.landing_override.approve')}
          canClearOverride={hasPermission('business_reports.landing_override.clear')}
          onTopBarActionsChange={setTopBarActions}
        />
        <BusinessRevenueOperationsWorkspace
          active={activeTab === 'business-revenue-operations'}
          token={token}
          userName={String(user?.username || user?.id || '')}
          centerAccess={businessReportCenterAccess}
          allCenterAccess={businessReportAllCenterAccess}
          canViewValidation={hasPermission('business_reports.validation.view')}
          canDecideValidation={hasPermission('business_reports.validation.decide')}
          canViewManual={hasPermission('business_reports.manual_revenue.view')}
          canManageManual={hasPermission('business_reports.manual_revenue.manage')}
          canApproveManual={hasPermission('business_reports.approve_manual_entries')}
          canImport={hasPermission('business_reports.import')}
          canManageImports={hasPermission('business_reports.manage')}
          canViewReconciliation={hasPermission('business_reports.reconciliation.view')}
          canExportReconciliation={hasPermission('business_reports.export')}
          canExportSensitiveReconciliation={hasPermission('business_reports.export') && hasPermission('business_reports.source_sensitive.export')}
          canManageReconciliation={hasPermission('business_reports.manage')}
          onTopBarActionsChange={setTopBarActions}
        />
        <BusinessDailyRevenueTab
          active={activeTab === DAILY_REVENUE_TAB && hasPermission('business_reports.daily_revenue.view')}
          token={token}
          canExport={hasPermission('business_reports.export')}
          canViewSensitiveEvidence={hasPermission('business_reports.source_sensitive.view')}
          canExportSensitiveEvidence={hasPermission('business_reports.source_sensitive.export')}
          canViewReconciliation={hasPermission('business_reports.reconciliation.view')}
          onTopBarActionsChange={setTopBarActions}
        />
        <BusinessMedicsRevenueTab
          active={activeTab === 'business-report:MEDICS_REVENUE'}
          token={token}
          canExport={hasPermission('business_reports.export')}
          onTopBarActionsChange={setTopBarActions}
        />
        <BusinessOperationalMonitoringTab
          active={activeTab === 'business-operational-monitoring'}
          token={token}
          canManage={hasPermission('business_reports.manage')}
          canRetry={hasPermission('business_reports.operations.retry')}
          userName={String(user?.username || user?.id || '')}
        />
        <BusinessDataIntegrityTab
          active={activeTab === 'business-data-integrity'}
          token={token}
          canExecute={hasPermission('business_reports.retention.execute')}
          onTopBarActionsChange={setTopBarActions}
        />
        <BusinessReportUnavailableTab
          active={isBusinessReportTab(activeTab) && !['business-report:DAILY_REVENUE_SNAPSHOT', 'business-report:MEDICS_REVENUE'].includes(activeTab)}
          report={isBusinessReportTab(activeTab) ? businessReports.find((report) => `business-report:${report.key}` === activeTab) : undefined}
        />
        <FeedbackTrackerTab
          active={activeTab === 'feedback-tracker'}
          token={token}
          centers={visibleCenters}
          generalSettings={generalSettings}
          permissions={{
            manual: hasPermission('feedback_tracker.manual'),
            sendMessage: hasPermission('feedback_tracker.send_message'),
            manage: hasPermission('feedback_tracker.manage'),
            assign: hasPermission('feedback_tracker.assign'),
            callback: hasPermission('feedback_tracker.callback'),
            escalate: hasPermission('feedback_tracker.escalate'),
            export: hasPermission('feedback_tracker.export'),
          }}
          notify={notifyFeedbackTracker}
          onTopBarActionsChange={setTopBarActions}
        />
        <OfferCampaignsTab active={activeTab === 'offer-campaigns'} token={token} centers={visibleCenters} canEnroll={hasPermission('offer_campaigns.enroll')} canManage={hasPermission('general_settings.manage_workflows')} notify={(message, type = 'info') => notify(message, type)} />
        <FeedbackResponsesTab active={activeTab === 'feedback'} rows={feedbackRows} pagination={feedbackPagination} centers={visibleCenters} isAdmin={isAdmin} canExport={hasPermission('feedback_responses.export')} feedbackCenter={centerFilter} journey={journeyFilter} bucket={bucketFilter} dateRange={feedbackDateRange} customFromDate={feedbackCustomFromDate} customToDate={feedbackCustomToDate} search={feedbackSearch} pageSize={feedbackPageSize} actions={actions} />
        <QuestionWiseTab active={activeTab === 'question-wise'} report={questionWise} centers={visibleCenters} center={centerFilter} days={questionWiseDaysFilter} customFromDate={questionWiseCustomFromDate} customToDate={questionWiseCustomToDate} journey={journeyFilter} canExport={hasPermission('question_wise_response.export')} actions={questionWiseActions} />
        <MonthlySummaryTab active={activeTab === 'summary'} rows={summaryRows} centers={visibleCenters} month={summaryMonth} center={summaryCenter} loaded={summaryLoaded} canExport={hasPermission('monthly_summary.export')} actions={actions} />
        <DetailedMonthlySummaryTab active={activeTab === 'detailed-summary'} rows={detailedSummaryRows} centers={visibleCenters} month={detailedSummaryMonth} search={detailedSummarySearch} center={detailedSummaryCenter} category={detailedSummaryCategory} appointmentStatus={detailedSummaryAppointmentStatus} feedbackStatus={detailedSummaryFeedbackStatus} loaded={detailedSummaryLoaded} actions={actions} />
        <StaffManagementTab active={activeTab === 'staff'} rows={staffRows} centers={visibleCenters} actions={actions} canCreate={hasPermission('staff_management.create')} canUpdate={hasPermission('staff_management.update')} canDelete={hasPermission('staff_management.delete')} />
        <UserAccountsTab active={activeTab === 'users'} rows={userRows} centers={visibleCenters} roles={assignableRoleRows} allRoles={roleRows} targetAccessOptions={targetAccessOptions} currentUserId={user?.id} canCreate={hasPermission('user_accounts.create')} canUpdate={hasPermission('user_accounts.update')} canDelete={hasPermission('user_accounts.delete')} adminOtpVerified={adminOtpVerified} actions={actions} />
        <SurveyQuestionsTab active={activeTab === 'questions'} rows={questionRows} actions={actions} canCreate={hasPermission('survey_questions.create')} canUpdate={hasPermission('survey_questions.update')} canDelete={hasPermission('survey_questions.delete')} />
        <RoleManagementTab active={activeTab === 'roles'} roles={roleRows} permissions={permissionRows} actions={actions} />
        <GeneralSettingsTab
          active={activeTab === 'general-settings'}
          data={generalSettings}
          integrationSettings={integrationSettings}
          canUpdate={hasPermission('general_settings.manage_workflows')}
          onSaveWorkflow={(key, payload) => runAction(async () => { await updateAutomationWorkflow(key, payload, token); await loadManagementData(); }, 'General settings workflow saved')}
          onCreateWorkflow={(payload) => runAction(async () => { await createAutomationWorkflow(payload, token); await loadManagementData(); }, 'General settings workflow created', 'Creating workflow...', { rethrow: true })}
          onCreateOfferCampaign={(payload: OfferCampaignAdminPayload & { campaignKey: string }) => runAction(async () => { await createOfferCampaign(payload, token); await loadManagementData(); }, 'Offer campaign workflow created', 'Creating offer campaign workflow...', { rethrow: true })}
          onCopyWorkflow={(key, payload) => runAction(async () => { await copyAutomationWorkflow(key, payload, token); await loadManagementData(); }, 'General settings workflow copied')}
          onActivateWorkflow={(key) => runAction(async () => { await activateAutomationWorkflow(key, token); await loadManagementData(); }, 'General settings workflow activated')}
          onDeactivateWorkflow={(key) => runAction(async () => { await deactivateAutomationWorkflow(key, token); await loadManagementData(); }, 'General settings workflow deactivated')}
          onDeleteWorkflow={(key) => runAction(async () => { await deleteAutomationWorkflow(key, token); await loadManagementData(); }, 'General settings workflow disabled')}
          onSaveGroup={(key, payload) => runAction(async () => { if (key) await updateGeneralSettingsGroup(key, payload, token); else await createGeneralSettingsGroup(payload, token); await loadManagementData(); }, 'General settings group saved', key ? 'Saving group...' : 'Creating group...', { rethrow: true })}
          onDeleteGroup={(key) => runAction(async () => { await deleteGeneralSettingsGroup(key, token); await loadManagementData(); }, 'General settings group deleted')}
          onNotify={notify}
        />
        <SystemSettingsTab active={activeTab === 'settings'} settings={integrationSettings} canUpdate={hasPermission('integration_settings.update')} canRotateSecrets={hasPermission('integration_settings.rotate_secret')} actions={actions} onTopBarActionsChange={setTopBarActions} />
      </AdminShell>
      {passwordChange.open && (
        <PasswordChangeModal
          state={passwordChange}
          onClose={closePasswordChange}
          onUpdate={updatePasswordChange}
          onSendOtp={() => void requestPasswordChangeOtp()}
          onConfirm={() => void submitPasswordChange()}
        />
      )}
      {modal && <AdminAlertModal message={modal.message} type={modal.type} title={modal.title} onClose={() => setModal(null)} />}
      {confirmMessage && confirmAction && <AdminConfirmModal message={confirmMessage} onCancel={() => { setConfirmMessage(''); setConfirmAction(null); }} onConfirm={() => { const action = confirmAction; setConfirmMessage(''); setConfirmAction(null); void action(); }} />}
    </>
  );
}

const passwordModalInputStyle = {
  width: '100%',
  height: 46,
  borderRadius: 14,
  border: '1px solid var(--border)',
  background: 'var(--surface-solid)',
  color: 'var(--text)',
  padding: '0 14px',
  fontSize: 14,
  fontWeight: 700,
  outline: 'none',
  boxSizing: 'border-box' as const,
};

function PasswordChangeModal({ state, onClose, onUpdate, onSendOtp, onConfirm }: {
  state: PasswordChangeState;
  onClose: () => void;
  onUpdate: (update: Partial<PasswordChangeState>) => void;
  onSendOtp: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="modal-overlay" style={{ display: 'flex', position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.62)', backdropFilter: 'blur(8px)', zIndex: 99999, alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="modal-content admin-premium-modal" role="dialog" aria-modal="true" aria-labelledby="password-change-title" style={{ width: 'min(94vw, 460px)', background: 'var(--surface-solid)', border: '1px solid var(--border)', borderRadius: 22, padding: 28, boxShadow: 'var(--shadow-lg)', position: 'relative' }}>
        {!state.forced && <button onClick={onClose} aria-label="Close password change" className="admin-modal-close">&times;</button>}
        <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
          <span style={{ width: 48, height: 48, borderRadius: 16, background: 'var(--primary-light)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 950, fontSize: 22 }}>#</span>
          <h3 id="password-change-title" style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 24, fontWeight: 950, color: 'var(--text)' }}>{state.forced ? 'Reset Password' : 'Change Password'}</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 650, lineHeight: 1.5 }}>
            {state.step === 'password'
              ? 'Enter a strong new password and choose where to receive your one-time verification code.'
              : `Enter the verification code received by ${state.channel === 'email' ? 'email' : 'WhatsApp (or SMS fallback)'}.`}
          </p>
        </div>
        {state.error && <div className="user-modal-error" role="alert">{state.error}</div>}
        {state.message && <div className="user-modal-error" style={{ background: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0' }}>{state.message}</div>}
        {state.step === 'password' ? (
          <div style={{ display: 'grid', gap: 14 }}>
            <div role="group" aria-label="Verification method" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button type="button" className={state.channel === 'email' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => onUpdate({ channel: 'email' })}>Email</button>
              <button type="button" className={state.channel === 'whatsapp' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => onUpdate({ channel: 'whatsapp' })}>WhatsApp</button>
            </div>
            <label className="input-group" style={{ margin: 0 }}>
              <span style={{ display: 'block', marginBottom: 6, fontFamily: 'Outfit, sans-serif', fontSize: 12, fontWeight: 850, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>New Password</span>
              <input type="password" autoComplete="new-password" value={state.newPassword} onChange={event => onUpdate({ newPassword: event.target.value, error: '' })} placeholder="Enter new password" style={passwordModalInputStyle} />
            </label>
            <PasswordRules password={state.newPassword} />
            <label className="input-group" style={{ margin: 0 }}>
              <span style={{ display: 'block', marginBottom: 6, fontFamily: 'Outfit, sans-serif', fontSize: 12, fontWeight: 850, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Confirm Password</span>
              <input type="password" autoComplete="new-password" value={state.confirmPassword} onChange={event => onUpdate({ confirmPassword: event.target.value, error: '' })} placeholder="Confirm new password" style={passwordModalInputStyle} />
            </label>
            <button type="button" className="btn btn-primary" disabled={state.sending} onClick={onSendOtp} style={{ width: '100%', borderRadius: 14, height: 48, fontWeight: 900 }}>{state.sending ? 'Sending Code...' : 'Continue'}</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <label className="input-group" style={{ margin: 0 }}>
              <span style={{ display: 'block', marginBottom: 6, fontFamily: 'Outfit, sans-serif', fontSize: 12, fontWeight: 850, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Verification Code</span>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" value={state.otp} onChange={event => onUpdate({ otp: event.target.value, error: '' })} placeholder="Enter OTP" style={passwordModalInputStyle} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.55fr) minmax(0, 1fr)', gap: 10 }}>
              <button type="button" className="btn btn-outline" disabled={state.submitting} onClick={() => onUpdate({ step: 'password', otp: '', error: '', message: '' })} style={{ borderRadius: 14, height: 48, fontWeight: 900 }}>Back</button>
              <button type="button" className="btn btn-primary" disabled={state.submitting} onClick={onConfirm} style={{ borderRadius: 14, height: 48, fontWeight: 900 }}>{state.submitting ? 'Verifying...' : 'Change Password'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PasswordRules({ password }: { password: string }) {
  const rules = [
    ['12 or more characters', password.length >= 12], ['Uppercase letter', /[A-Z]/.test(password)],
    ['Lowercase letter', /[a-z]/.test(password)], ['Number', /\d/.test(password)], ['Special character', /[^A-Za-z0-9]/.test(password)],
  ] as const;
  const score = rules.filter(([, met]) => met).length;
  const strengthLabel = !password ? 'Enter a password' : score === rules.length ? 'Strong - accepted' : score >= 3 ? 'Almost there' : 'Too weak';
  return <><div className="password-strength" data-level={score} aria-live="polite"><div className="password-strength-header"><span>Password strength</span><strong>{strengthLabel}</strong></div><div className="password-strength-track" aria-hidden="true">{rules.map(([label], index) => <i key={label} className={score > index ? 'active' : ''} />)}</div></div><div className="password-rule-grid" aria-label="Password requirements">{rules.map(([label, met]) => <span key={label} style={{ color: met ? '#16834a' : 'var(--text-muted)', fontSize: 12, fontWeight: 700 }}>{met ? '✓' : '○'} {label}</span>)}</div></>;
}

function displayNameFallback(user: AdminUserSession['user'] | undefined): string {
  const explicit = user?.display_name?.trim();
  if (explicit) return explicit;
  const fullName = [user?.first_name, user?.middle_name, user?.last_name].map(part => part?.trim()).filter(Boolean).join(' ');
  return fullName || user?.username || 'Admin User';
}

function firstNameForGreeting(user: AdminUserSession['user'] | undefined): string {
  const firstName = user?.first_name?.trim();
  if (firstName) return firstName;
  const display = displayNameFallback(user);
  return display.split(/\s+/).filter(Boolean)[0] || 'Admin';
}

function labelFromRoleKey(value: string): string {
  return value.split(/[_\s-]+/).filter(Boolean).map(part => `${part[0]?.toUpperCase() || ''}${part.slice(1).toLowerCase()}`).join(' ') || 'Reports';
}

function readableMonth(month: string) {
  if (!month.includes('-')) return month || 'Summary';
  try {
    const [year, monthIndex] = month.split('-');
    const value = new Date(Number.parseInt(year), Number.parseInt(monthIndex) - 1, 1);
    return value.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return month || 'Summary';
  }
}

function prepareSummaryPdfClone(content: HTMLElement, monthLabel: string) {
  content.style.cssText = 'width:100% !important;max-width:100% !important;min-width:0 !important;padding:0 !important;margin:0 !important;box-sizing:border-box !important;background:#ffffff !important;overflow:visible !important;box-shadow:none !important;border-radius:0 !important;font-family:"Inter",sans-serif !important;color:#0f172a !important;';

  const oldHeader = content.querySelector('h3');
  if (oldHeader) oldHeader.remove();

  const headerBanner = content.ownerDocument.createElement('div');
  headerBanner.style.cssText = 'display:flex;justify-content:space-between;align-items:center;border-bottom:3.5px solid #e91e63;padding-bottom:20px;margin-bottom:24px;';
  const titleWrap = content.ownerDocument.createElement('div');
  const title = content.ownerDocument.createElement('h1');
  title.style.cssText = "font-family:'Outfit',sans-serif;font-size:22px;font-weight:800;color:#0f172a;margin:0;text-transform:uppercase;";
  title.textContent = 'Monthly Performance Summary';
  const subtitle = content.ownerDocument.createElement('p');
  subtitle.style.cssText = "font-size:12px;color:#64748b;font-weight:600;margin:6px 0 0 0;font-family:'Outfit';";
  subtitle.textContent = 'Ferty9 Fertility Center - Patient Experience NPS Analytics';
  titleWrap.append(title, subtitle);

  const metaWrap = content.ownerDocument.createElement('div');
  metaWrap.style.cssText = 'text-align:right;';
  const monthBadge = content.ownerDocument.createElement('span');
  monthBadge.style.cssText = "display:inline-block;background:#fce4ec;color:#e91e63;font-size:11px;font-weight:800;padding:6px 12px;border-radius:24px;text-transform:uppercase;font-family:'Outfit';";
  monthBadge.textContent = monthLabel;
  const generated = content.ownerDocument.createElement('p');
  generated.style.cssText = 'font-size:10px;color:#94a3b8;font-weight:500;margin:6px 0 0 0;';
  generated.textContent = `Report Generated: ${new Date().toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`;
  metaWrap.append(monthBadge, generated);
  headerBanner.append(titleWrap, metaWrap);
  content.insertBefore(headerBanner, content.firstChild);

  const table = content.querySelector('#summary-table') as HTMLElement | null;
  if (!table) return;

  const oldColgroup = table.querySelector('colgroup');
  if (oldColgroup) oldColgroup.remove();

  const colgroup = table.ownerDocument.createElement('colgroup');
  ['16%', '6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%'].forEach((width) => {
    const col = table.ownerDocument.createElement('col');
    col.style.cssText = `width: ${width} !important;`;
    colgroup.appendChild(col);
  });
  table.insertBefore(colgroup, table.firstChild);

  table.style.cssText = 'width:100% !important;min-width:0 !important;max-width:100% !important;border-collapse:collapse !important;font-family:"Inter",sans-serif !important;border:1.5px solid #cbd5e1 !important;margin:0 !important;table-layout:fixed !important;';

  const headerRows = table.querySelectorAll('thead tr');
  if (headerRows.length >= 2) {
    const firstRow = headerRows[0] as HTMLElement;
    firstRow.style.cssText = 'background:#f8fafc !important;';
    firstRow.querySelectorAll('th').forEach((th, index) => {
      const cell = th as HTMLElement;
      cell.style.cssText = 'padding:10px 4px !important;font-size:9px !important;font-weight:800 !important;text-transform:uppercase !important;word-break:break-word !important;text-align:center !important;vertical-align:middle !important;border:1.5px solid #cbd5e1 !important;';
      if (index === 0) {
        th.removeAttribute('rowspan');
        cell.innerText = 'CENTRE';
        cell.style.cssText += 'width:16% !important;background:#e2e8f0 !important;color:#0f172a !important;border-bottom:none !important;padding-bottom:2px !important;vertical-align:bottom !important;';
      } else if (index === 1) {
        cell.style.cssText += 'width:28% !important;background:#7dd3fc !important;color:#0369a1 !important;';
      } else if (index === 2) {
        cell.style.cssText += 'width:28% !important;background:#fbcfe8 !important;color:#9d174d !important;';
      } else if (index === 3) {
        cell.style.cssText += 'width:28% !important;background:#fed7aa !important;color:#9a3412 !important;';
      }
    });

    const secondRow = headerRows[1] as HTMLElement;
    const centreBottom = table.ownerDocument.createElement('th');
    centreBottom.innerText = 'NAME';
    centreBottom.style.cssText = 'width:16% !important;background:#e2e8f0 !important;color:#0f172a !important;padding:2px 4px 8px 4px !important;font-size:9px !important;font-weight:800 !important;text-transform:uppercase !important;word-break:break-word !important;text-align:center !important;vertical-align:top !important;border:1.5px solid #cbd5e1 !important;border-top:none !important;';
    secondRow.insertBefore(centreBottom, secondRow.firstChild);

    const subWidths = ['6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%'];
    secondRow.querySelectorAll('th').forEach((th, index) => {
      if (index === 0) return;
      const width = subWidths[index - 1] || '7%';
      (th as HTMLElement).style.cssText = `width:${width} !important;padding:8px 3px !important;font-size:8px !important;font-weight:700 !important;color:#475569 !important;text-transform:uppercase !important;border:1.5px solid #cbd5e1 !important;background:#f8fafc !important;word-break:break-word !important;text-align:center !important;vertical-align:middle !important;`;
    });
  }

  table.querySelectorAll('tbody tr').forEach((row, rowIndex) => {
    const tr = row as HTMLElement;
    tr.style.background = rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc';
    tr.querySelectorAll('td').forEach((cell, cellIndex) => {
      const td = cell as HTMLElement;
      td.style.cssText = 'padding:8px 3px !important;font-size:8px !important;border:1.5px solid #cbd5e1 !important;color:#334155 !important;font-weight:600 !important;text-align:center !important;word-break:break-word !important;';
      if (cellIndex === 0) {
        td.style.cssText += 'width:16% !important;text-align:left !important;font-weight:800 !important;color:#0f172a !important;background:#f8fafc !important;padding-left:8px !important;';
      } else {
        const bodyWidths = ['6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%'];
        td.style.cssText += `width:${bodyWidths[cellIndex - 1]} !important;`;
      }
      if (cellIndex === 4 || cellIndex === 8 || cellIndex === 12) {
        const score = Number.parseFloat(td.innerText) || 0;
        td.style.fontWeight = '800';
        if (score > 0) {
          td.style.color = '#15803d';
          td.style.background = '#f0fdf4';
        } else if (score < 0) {
          td.style.color = '#b91c1c';
          td.style.background = '#fef2f2';
        }
      }
    });
  });

  const footerRow = table.querySelector('tfoot tr') as HTMLElement | null;
  if (footerRow) {
    footerRow.style.cssText = 'background:linear-gradient(135deg,#e91e63,#c2185b) !important;color:#ffffff !important;';
    footerRow.querySelectorAll('td').forEach((cell, cellIndex) => {
      const td = cell as HTMLElement;
      td.style.cssText = 'padding:10px 3px !important;font-size:9px !important;font-weight:800 !important;text-align:center !important;color:#ffffff !important;border:none !important;';
      if (cellIndex === 0) {
        td.style.cssText += 'width:16% !important;text-align:left !important;padding-left:8px !important;';
      } else {
        const bodyWidths = ['6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%', '6%', '8%', '8%', '6%'];
        td.style.cssText += `width:${bodyWidths[cellIndex - 1]} !important;`;
      }
    });
  }
}
